import { useEffect, useRef, useState } from 'react';
import AvisoModal from 'components/AvisoModal';
import VotoValePor from './VotoValePor';
import styles from './VoteModal.module.css';

const parseOpts = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  if (typeof val === 'string') {
    try { const p = JSON.parse(val); if (Array.isArray(p)) return p.filter(Boolean); } catch {}
    return val.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
};

const RESPUESTAS = { favor: 'A favor', contra: 'En contra', abstencion: 'Abstención' };

export default function VoteModal({ question, incompleta, onVote, onClose, voting, voto, aviso }) {
  const [multiSel, setMultiSel] = useState(new Set());
  // El error del voto llega como la alerta de la PÁGINA (`aviso`), que queda TAPADA por este cuadro. Aquí se muestra DENTRO y se queda
  // HASTA la próxima acción del votante (elegir otra respuesta o confirmar de nuevo): la alerta de la página se borra sola a los 4–9 s,
  // poco para leer «tu voto no se guardó». Al salir bien el voto el cuadro se cierra, así que el éxito no se muestra aquí.
  const [avisoLocal, setAvisoLocal] = useState(null);
  useEffect(() => { setAvisoLocal(null); }, [question.id]);
  // Solo un aviso NUEVO, llegado con el cuadro ya abierto: si el cuadro se reabre solo (el sondeo vuelve a abrir la pregunta pendiente) mientras
  // la alerta vieja de la página sigue viva, esa no debe aparecer de la nada. Cada alerta es un objeto nuevo, así que basta comparar con el inicial.
  const avisoInicial = useRef(aviso);
  useEffect(() => { if (aviso && aviso !== avisoInicial.current && aviso.type !== 'success') setAvisoLocal(aviso); }, [aviso]);
  const votar = (id, valor) => { setAvisoLocal(null); return onVote(id, valor); };
  const opts    = parseOpts(question.options);
  const maxC    = question.max_choices || opts.length;
  const isMulti = question.question_type === 'multiple_choice';
  const isOne   = question.question_type === 'one_choice';

  const toggleMulti = (opt) => {
    setAvisoLocal(null);
    setMultiSel(prev => {
      const next = new Set(prev);
      next.has(opt) ? next.delete(opt) : next.add(opt);
      return next;
    });
  };

  // Sí/no y opción única: tocar una respuesta solo la MARCA; el voto se envía al
  // confirmar (un toque equivocado en el celular ya no vota). Se guarda con el id de
  // la pregunta para no arrastrar la selección a otra pregunta.
  const [sel, setSel] = useState(null);
  const seleccion = sel && sel.qid === question.id ? sel.value : null;
  const elegir = (value) => { setAvisoLocal(null); setSel({ qid: question.id, value }); };
  const enviando = voting === question.id;

  const bloqueConfirmar = (etiqueta) => (
    <div className={styles.confirmar}>
      <div aria-live="polite" className={styles.confirmarEstado}>
        {seleccion
          ? <>Tu respuesta: <strong className={styles.confirmarRespuesta}>{etiqueta}</strong></>
          : 'Elige una respuesta para continuar.'}
      </div>
      <button type="button" className={`btn btn-primary ${styles.botonConfirmar}`}
        onClick={() => votar(question.id, seleccion)} disabled={!seleccion || enviando}>
        {enviando
          ? <><i className="fas fa-spinner fa-spin" /> Votando...</>
          : <><i className="fas fa-check-to-slot" /> Confirmar mi voto</>}
      </button>
      <p className={styles.confirmarNota}>
        Después de confirmar no podrás cambiar tu voto.
      </p>
    </div>
  );

  return (
    <div className={`vote-modal-overlay ${styles.overlay}`}>
      <div className={styles.caja}>
        <div className={styles.cabecera}>
          <div className={styles.cabeceraFila}>
            <span className={styles.cabeceraEtiqueta}>
              <i className={`fas fa-circle ${styles.cabeceraPunto}`} />Pregunta activa
            </span>
            <button onClick={onClose} className={styles.cerrar}>✕</button>
          </div>
          <h2 className={styles.pregunta}>
            {question.question_text}
          </h2>
          <VotoValePor voto={voto} variante="modal" />
        </div>

        <div className={styles.cuerpo}>
          {avisoLocal && <AvisoModal tipo={avisoLocal.type}>{avisoLocal.message}</AvisoModal>}
          {/* F-21: el voto se guardó solo por algunas unidades — se completa con
              la MISMA respuesta (el servidor también lo garantiza). */}
          {incompleta && (
            <div className={styles.incompleta}>
              <div className={styles.incompletaAviso}>
                <i className={`fas fa-triangle-exclamation ${styles.icono6}`} />
                Tu voto quedó <strong>incompleto</strong>: se registró por {incompleta.votadas} de {incompleta.total} unidades por un problema momentáneo del servidor.
                Las que faltan se registran con la misma respuesta: <strong>{RESPUESTAS[incompleta.answer] || incompleta.answer}</strong>.
              </div>
              <button className={`btn btn-primary ${styles.botonCentrado}`}
                onClick={() => votar(question.id, incompleta.answer)} disabled={voting === question.id}>
                {voting === question.id
                  ? <><i className="fas fa-spinner fa-spin" /> Votando...</>
                  : <><i className="fas fa-check-to-slot" /> Completar mi voto</>}
              </button>
            </div>
          )}

          {/* Yes/No */}
          {!incompleta && question.question_type === 'yes_no' && (
            <>
              <div className={`voto-opciones ${styles.opciones}`}>
                {[
                  { value: 'favor',      label: 'A favor',    icon: 'fa-thumbs-up',   bg: '#12b76a', sh: 'rgba(18,183,106,0.3)' },
                  { value: 'contra',     label: 'En contra',  icon: 'fa-thumbs-down', bg: '#f04438', sh: 'rgba(240,68,56,0.3)' },
                  { value: 'abstencion', label: 'Abstención', icon: 'fa-minus',       bg: '#8a92a6', sh: 'rgba(138,146,166,0.3)' },
                ].map(opt => {
                  const marcada = seleccion === opt.value;
                  return (
                    <button key={opt.value} type="button" onClick={() => elegir(opt.value)}
                      disabled={enviando} aria-pressed={marcada}
                      className={`${styles.respuesta} ${marcada ? styles.respuestaMarcada : ''} ${enviando ? styles.respuestaEnviando : (seleccion && !marcada ? styles.respuestaAtenuada : '')}`}
                      style={{ '--tono': opt.bg, '--sombra': opt.sh }}>
                      <i className={`fas ${marcada ? 'fa-circle-check' : opt.icon} ${styles.respuestaIcono}`} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {bloqueConfirmar(RESPUESTAS[seleccion])}
            </>
          )}

          {/* One/Multiple choice */}
          {!incompleta && (isOne || isMulti) && (
            <div className={styles.lista}>
              {isMulti && (
                <div className={styles.listaAyuda}>
                  Selecciona hasta <strong>{maxC}</strong> opción{maxC !== 1 ? 'es' : ''} · {multiSel.size}/{maxC}
                </div>
              )}
              {opts.map((opt, idx) => {
                const isSel = isMulti ? multiSel.has(opt) : seleccion === opt;
                return (
                  <button key={idx} type="button" aria-pressed={isSel}
                    onClick={() => isOne ? elegir(opt) : toggleMulti(opt)}
                    disabled={enviando || (isMulti && !isSel && multiSel.size >= maxC)}
                    className={`${styles.opcion} ${isSel ? styles.opcionSel : ''} ${(isMulti && !isSel && multiSel.size >= maxC) ? styles.opcionLimite : ''}`}>
                    <span className={`${styles.marca} ${isMulti ? styles.marcaMulti : ''} ${isSel ? styles.marcaSel : ''}`}>
                      {isSel && <i className={`fas fa-check ${styles.marcaCheck}`} />}
                    </span>
                    <span className={`${styles.opcionTexto} ${isSel ? styles.opcionTextoSel : ''}`}>
                      {opt}
                    </span>
                  </button>
                );
              })}
              {isOne && bloqueConfirmar(seleccion)}
              {isMulti && (
                <button className={`btn btn-primary ${styles.botonMulti}`}
                  onClick={() => votar(question.id, Array.from(multiSel).join(','))}
                  disabled={multiSel.size === 0 || multiSel.size > maxC || voting === question.id}>
                  {voting === question.id
                    ? <><i className="fas fa-spinner fa-spin" /> Votando...</>
                    : <><i className="fas fa-check-to-slot" /> Confirmar ({multiSel.size})</>}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
